import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, useOrg } from "@/App";
import {
  LayoutDashboard,
  FileText,
  Users,
  Package,
  Boxes,
  Calculator,
  CreditCard,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  ChevronDown,
  FolderTree
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { currentOrg, organizations, switchOrg } = useOrg();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/dashboard/invoices", icon: FileText, label: "Invoices" },
    { path: "/dashboard/customers", icon: Users, label: "Customers" },
    { path: "/dashboard/items", icon: Package, label: "Items" },
    { path: "/dashboard/categories", icon: FolderTree, label: "Categories" },
    { path: "/dashboard/inventory", icon: Boxes, label: "Inventory" },
    { path: "/dashboard/taxes", icon: Calculator, label: "Taxes" },
    { path: "/dashboard/payments", icon: CreditCard, label: "Payments" },
    { path: "/dashboard/reports", icon: BarChart3, label: "Reports" },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-zinc-50 border-r border-zinc-200 transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-200">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-semibold text-lg">InvoiceHub</span>
            </Link>
            <button 
              className="lg:hidden p-2"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Organization Switcher */}
          {currentOrg && (
            <div className="px-4 py-3 border-b border-zinc-200">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-between px-3 h-auto py-2"
                    data-testid="org-switcher"
                  >
                    <div className="flex items-center gap-2 text-left">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <div className="truncate">
                        <p className="text-sm font-medium truncate">{currentOrg.name}</p>
                        <p className="text-xs text-muted-foreground">Organization</p>
                      </div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {organizations.map((org) => (
                    <DropdownMenuItem
                      key={org.org_id}
                      onClick={() => switchOrg(org.org_id)}
                      className={org.org_id === currentOrg.org_id ? "bg-accent" : ""}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      {org.name}
                      {org.is_default && <span className="ml-auto text-xs text-muted-foreground">Default</span>}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard/organizations")}>
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Organizations
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2 text-sm transition-colors
                  ${isActive(item.path, item.exact) 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-zinc-600 hover:bg-zinc-100'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Bottom section */}
          <div className="p-4 border-t border-zinc-200">
            <Link
              to="/dashboard/settings"
              className={`
                flex items-center gap-3 px-3 py-2 text-sm transition-colors mb-2
                ${isActive('/dashboard/settings') 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-zinc-600 hover:bg-zinc-100'
                }
              `}
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-zinc-600 hover:bg-zinc-100"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <button 
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu">
                <div className="w-8 h-8 bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span className="hidden sm:inline text-sm">{user?.name}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
